<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          {{ $t('customer.contact.customersContacts') }}
        </span>
        <h3 class="page-title">
          {{ $t('customer.contact.editTitle') }}
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/kontakty-z-klientami')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button
          theme="success"
          :disabled="!can_edit"
          @click.prevent="validateForm">
          <i class="material-icons">edit</i>
          {{ $t('ui.saveChanges') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <validation-observer ref="contact-edit-form" tag="form">
        <d-card class="mb-5">
          <d-tabs card v-if="!loadError && allDataLoaded">
            <d-tab
              :title="$t('ui.generalInfo')"
              active>
              <h4 class="mb-3 pb-3 border-bottom d-flex">
                {{ $t('ui.generalInfo') }}

                <div
                  v-if="communication && communication.id"
                  class="ml-auto">
                  <small>
                    {{ $t('inquiries.inquiryStatus') }}:
                    <strong
                      :class="{
                        'text-success': communication.customer_status === 'ACCEPTED',
                        'text-danger': communication.customer_status === 'REJECTED'
                      }">
                      <template v-if="communication.customer_status">
                        {{ $t(`complaints.statusNames.${communication.customer_status.toLowerCase()}`) }}
                      </template>
                      <template v-else>
                        {{ $t('ui.lack') }}
                      </template>
                    </strong>
                  </small>
                </div>
              </h4>

              <div class="form-group">
                <label>
                  {{ $t('customer.contact.contactDate') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="date"
                  v-slot="{ errors }">
                  <d-datepicker
                    v-model="date"
                    :monday-first="true"
                    format="yyyy-MM-dd"
                    :placeholder="$t('ui.chooseDate')"
                    :disabled-dates="futureDates"
                    :disabled="!can_edit"
                    :language="translationsForDatePicker" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('users.customer') }}:
                </label>

                <template v-if="can_edit">
                  <select-customer
                    v-if="allDataLoaded"
                    :customerID="customer_id" />
                </template>
                <template v-else>
                  <div class="text-primary">
                    {{ customer_name }}
                  </div>
                </template>

                <div
                  v-if="triedToSend && !customerIsValid"
                  class="text-danger">
                  {{ $t('ui.thisFieldIsRequired') }}
                </div>
              </div>

              <div
                v-if="customer_id"
                class="form-group">
                <label for="name">
                  {{ $t('customer.contact.contactRelatedToOrder') }}:
                </label>

                <template v-if="can_edit">
                  <select-order
                    v-if="allDataLoaded"
                    ref="orderSelect"
                    :orderID="order_id"
                    :customerID="customer_id"/>
                </template>
                <template v-else>
                  <div class="text-primary">
                    {{ order_name }}
                  </div>
                </template>
              </div>

              <div
                v-if="customer_id"
                class="form-group">
                <label for="name">
                  {{ $t('customer.contact.contactRelatedToContact') }}:
                </label>

                <d-button
                  v-if="!contactIsEditable && customer_contact && can_edit"
                  theme="primary"
                  size="sm"
                  class="ml-2"
                  @click="contactIsEditable = true">
                  <i class="material-icons">edit</i>
                  {{ $t('ui.edit') }}
                </d-button>

                <contact-customer-contact
                  v-show="contactIsEditable || !customer_contact"
                  ref="contactSelect"
                  :customerId="customer_id"
                  :readOnly="!can_edit" />

                <div
                  v-if="!contactIsEditable && customer_contact"
                  class="mt-3 mb-4">
                  {{ customer_contact.name }}
                  <br v-if="customer_contact.name">
                  {{ customer_contact.email }}
                  <br v-if="customer_contact.email">
                  {{ customer_contact.phone }}
                </div>
              </div>

              <div class="form-group">
                <label class="d-block">
                  {{ $t('customer.contact.contactType') }}:
                </label>
                <d-form-checkbox
                  v-for="(contactType, index) of contactTypes"
                  :key="'contact-type-checkbox-' + index"
                  inline
                  v-model="types"
                  :value="contactType.type"
                  :disabled="!can_edit">
                  {{ contactType.name }}
                </d-form-checkbox>
              </div>

              <div class="form-group">
                <label for="name">
                  {{ $t('customer.contact.descriptionOptional') }}:
                </label>

                <d-textarea
                  :value.prop="description"
                  :disabled="!can_edit"
                  v-model="description" />
              </div>

              <div
                v-if="communication && communication.id"
                class="form-group">
                <label for="customer-descirption">
                  {{ $t('customer.contact.customerResponse') }}:
                </label>

                <template v-if="communication.customer_answer_at">
                  <div class="py-2">
                    {{ communication.customer_description }}
                  </div>
                  <small class="form-text text-muted">
                    {{ $t('customer.contact.repliedOn') }} {{ dateAsString(communication.customer_answer_at) }}
                  </small>
                </template>
                <template v-else>
                  <div>{{ $t('customer.contact.noResponse') }}</div>
                </template>
              </div>
            </d-tab>
            <d-tab
              :title="$t('customer.contact.comments')">
              <contact-comments
                v-if="!loadError && allDataLoaded"
                :comments="comments"
                :contact-id="parseInt(this.$route.params.id, 10)" />
            </d-tab>
            <d-tab
              :title="$t('customer.contact.notifications')">
              <contact-notifications
                v-if="!loadError && allDataLoaded"
                :notifications="notifications"
                :contactID="parseInt(this.$route.params.id, 10)" />
            </d-tab>
          </d-tabs>

          <div v-if="!allDataLoaded && !loadError" class="pb-3 pt-3 text-center overlay-loading">
            {{ $t('ui.dataLoading') }}&hellip;
          </div>

          <d-alert
            v-if="loadError"
            show
            theme="warning">
            {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
          </d-alert>
        </d-card>
      </validation-observer>
    </d-form>
  </d-container>
</template>

<script>
import ContactCustomerContact from './ContactCustomerContact.vue';
import ContactNotifications from './ContactNotifications.vue';
import datepickerTranslations from './../../data/datepicker-translations.js';
import FormUtils from './../../utils/FormUtils.js';
import SelectCustomer from './../../components/common/SelectCustomer.vue';
import SelectOrder from './../../components/common/SelectOrder.vue';
import Vue from 'vue';
import getPrefferedLanguage from './../../utils/i18nUtils.js';
import ContactComments from '@/components/contacts/ContactComments.vue';

export default {
  name: 'contact-edit',
  components: {
    'contact-notifications': ContactNotifications,
    'contact-customer-contact': ContactCustomerContact,
    'select-customer': SelectCustomer,
    'select-order': SelectOrder,
    'contact-comments': ContactComments
  },
  computed: {
    allDataLoaded () {
      return this.loadedData && this.loadedTypes;
    },
    translationsForDatePicker () {
      return datepickerTranslations[getPrefferedLanguage()];
    },
    futureDates () {
      return {
        from: new Date()
      };
    }
  },
  watch: {
    'customer_id': function (newValue) {
      if (newValue) {
        this.customerIsValid = true;
      } else {
        this.customerIsValid = false;
      }
    }
  },
  data() {
    return {
      loadedData: false,
      date: new Date(),
      customer_id: null,
      customer_contact_id: null,
      order_id: null,
      types: ['PHONE'],
      description: '',
      customer_name: '',
      order_name: '',
      can_edit: false,
      communication: {},
      comments: [],
      customer_contact: {},
      notifications: [],
      loadError: false,
      loadedTypes: false,
      contactTypes: [],
      customerIsValid: false,
      triedToSend: false,
      setupIsDone: false,
      contactIsEditable: false
    };
  },
  mounted () {
    this.loadItemData();
    this.loadAdditionalData();
    this.$bus.$on('comments-reload-needed', this.reloadCommentsView);
    this.$bus.$on('contact-notifications-reload-needed', this.reloadNotificationsView);
    this.$bus.$on('ui-select-customer-updated', this.updateCustomer);
    this.$bus.$on('ui-select-order-updated', this.updateOrder);
    this.$bus.$on('ui-select-customer-contact-updated', this.updateCustomerContact);
  },
  methods: {
    reloadCommentsView () {
      this.loadedData = false;

      FormUtils.loadItemData(this, {
        endpointBase: '/contacts/item/',
        instanceFields: ['comments'],
        loadedKey: 'loadedData',
        errorKey: 'loadError'
      });
    },
    reloadNotificationsView () {
      this.loadedData = false;

      FormUtils.loadItemData(this, {
        endpointBase: '/contacts/item/',
        instanceFields: ['notifications'],
        loadedKey: 'loadedData',
        errorKey: 'loadError'
      });
    },
    loadItemData () {
      FormUtils.loadItemData(this, {
        endpointBase: '/contacts/item/',
        instanceFields: ['customer_id', 'customer_contact_id', 'customer_contact', 'order_id', 'types', 'description', 'customer_name', 'order_name', 'can_edit', 'communication', 'comments', 'notifications'],
        booleanFields: ['can_edit'],
        loadedKey: 'loadedData',
        errorKey: 'loadError',
        successAction: (response) => {
          if (response) {
            let dateString = response.date;
            Vue.set(this, 'date', new Date(dateString));

            Vue.nextTick(() => {
              if (this.customer_contact_id) {
                if (this.$refs['contactSelect']) {
                  this.$refs['contactSelect'].setContactID(this.customer_contact_id);
                }
              }

              this.setupIsDone = true;
            });
          }
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-contact-types/items',
        loadedKey: 'loadedTypes',
        outputKey: 'contactTypes',
        errorKey: 'loadError'
      });
    },
    validateForm () {
      this.triedToSend = true;

      if (!this.customer_id) {
        return;
      }

      FormUtils.validate(this.$refs['contact-edit-form'], this.submitForm);
    },
    submitForm () {
      let formDataToSend = {
        date: [this.date.getFullYear(), this.date.getMonth() + 1, this.date.getDate()].map(n => n < 10 ? '0' + n : n).join('-'),
        customer_id: this.customer_id,
        types: this.types,
        description: this.description
      };

      if (this.order_id) {
        formDataToSend.order_id = parseInt(this.order_id, 10);
      } else {
        formDataToSend.order_id = null;
      }

      if (this.customer_contact_id) {
        formDataToSend.customer_contact_id = this.customer_contact_id;
      } else {
        formDataToSend.customer_contact_id = null;
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/contacts/update/' + parseInt(this.$route.params.id, 10),
        formData: formDataToSend,
        successTitle: this.$t('customer.contact.editContactSuccessTitle'),
        successText: this.$t('customer.contact.editContactSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('customer.contact.editContactErrorText'),
        successAction: () => {
          this.$router.push('/kontakty-z-klientami/');
        }
      });
    },
    updateCustomer (newID) {
      this.customer_id = newID;

      if (this.setupIsDone) {
        if (newID) {
          this.contactIsEditable = true;

          Vue.nextTick(() => {
            this.$refs['orderSelect'].resetValue();
            this.$refs['orderSelect'].loadOrdersForCustomer();
            this.$refs['contactSelect'].fetchContacts();
          });
        } else {
          this.order_id = null;
          this.customer_contact_id = null;
        }
      }
    },
    updateOrder (newID) {
      this.order_id = newID;
    },
    updateCustomerContact (newID) {
      this.customer_contact_id = newID;
    },
    dateAsString (dateString) {
      if (!dateString) {
        return '';
      }

      let date = new Date(dateString);

      return [date.getFullYear(), date.getMonth() + 1, date.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
  },
  beforeDestroy () {
    this.$bus.$off('ui-select-customer-updated', this.updateCustomer);
    this.$bus.$off('ui-select-order-updated', this.updateOrder);
    this.$bus.$off('ui-select-customer-contact-updated', this.updateCustomerContact);
    this.$bus.$off('comments-reload-needed', this.reloadCommentsView);
    this.$bus.$off('contact-notifications-reload-needed', this.reloadNotificationsView);
  }
};
</script>
